import {
    ArrayUtil, AutomationUtil, Block, Color, DOMUtil, FieldUpdateEvent, getThemeColor, getThemeForKey, HeightWidth,
    Keys, ModelRow, ScrollOptions
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AddType } from "./AddType";
import { BehaviorTypes } from "../../base/BehaviorTypes";
import { Button } from "../button/Button";
import { ButtonProps } from "../button/ButtonProps";
import { ButtonVariant } from "../button/ButtonVariant";
import { ClickEvent } from "../../events/ClickEvent";
import { CloneComponent } from "../../base/CloneComponent";
import { Component } from "../../base/Component";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ComponentDeserializer, DeserializeProps } from "../../serializer/ComponentDeserializer";
import { Container } from "../../base/Container";
import { ContextMenuItemsProvider, ContextMenuLabelProps } from "../contextmenu/ContextMenuLabelProps";
import { DataSourceMode } from "../../databinding/DataSource";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { DesignerInterface } from "../../base/DesignerInterface";
import { DomEvent, DomMouseEvent } from "../../events/DomEvent";
import { EditRowDecorator } from "../../page/decorators/EditRowDecorator";
import { Event } from "../../events/Event";
import { ExpandButton } from "./ExpandButton";
import { ExpandTableCell } from "./ExpandTableCell";
import { KeyEvent } from "../../events/KeyEvent";
import { KeyHandler } from "../../keyhandling/KeyHandler";
import { KeyHandlerGroup } from "../../keyhandling/KeyHandlerGroup";
import { Layout } from "../layout/Layout";
import { MouseEvent } from "../../events/MouseEvent";
import { RowModeControlType } from "./RowModeControlType";
import { SelectCheckboxTableCell } from "./SelectCheckboxTableCell";
import { SelectionMode, SelectionType } from "../../base/SelectionMode";
import { Table } from "./Table";
import { TableAction, TableContentsChangedEvent } from "../../events/TableContentsChangedEvent";
import { TableCell } from "./TableCell";
import { TableColumn } from "./TableColumn";
import { TableRowBackgroundColors } from "./TableRowBackgroundColors";
import { TableRowBeforeSaveEvent } from "../../events/TableRowBeforeSaveEvent";
import { TableRowBehavior } from "./TableRowBehavior";
import { TableRowCreationEvent } from "../../events/TableRowCreationEvent";
import { TableRowDisplayEvent } from "../../events/TableRowDisplayEvent";
import { TableRowMode } from "./TableRowMode";
import { TableRowModeChangeEvent } from "../../events/TableRowModeChangeEvent";
import { TableRowPropDefinitions, TableRowProps } from "./TableRowProps";
import { TableRowStyles } from "./TableRowStyles";
import { Textbox } from "../textbox/Textbox";
import { ToolsTableCell } from "./ToolsTableCell";
import { ValidationResult } from "../../base/ValidationResult";

const log = DesignableObjectLogManager.getLogger("components.table.TableRow");

export class TableRow extends Component implements TableRowProps {
    private static _rowBehavior: TableRowBehavior;
    private _table: Table;
    private _allowDelete: boolean;
    private _allowEdit: boolean;
    private _backgroundColors: TableRowBackgroundColors;
    private _populatedDOM: boolean;

    private expandCell: ExpandTableCell; // The cell that contains the expand button
    private _expansionComponent: Component; // The Component (Panel) that is the content of the row expansion area
    private _expanded: boolean;
    private _expandPromise: Promise<Component | null> = Promise.resolve(null);
    private _expansionElement: HTMLTableRowElement;

    private _cells: TableCell[];
    private _placeholderHeightWidth: HeightWidth;
    private placeholder: HTMLTableCellElement;
    private _expandable: boolean;
    private _data: any;
    private _originalData: any;
    private _index: number;
    private _selected: boolean;
    private _mode: TableRowMode;
    private _toolsCell: ToolsTableCell;
    public virtualized: boolean;
    private _unexpandedBorderBottomWidth: string;
    private _dragSession: DragSession;
    private _keyHandlerGroup: KeyHandlerGroup;
    private hiddenCells: TableCell[];
    private _populatePromise: Promise<void> | null = null;
    private _additionalToolButtons: Button[];

    public static create(table: Table, props?: Partial<TableRowProps>): TableRow {
        return ComponentTypes.createComponentOfType("tablerow", { table: table, ...props });
    }

    protected constructor(props?: Partial<TableRowProps>) {
        super("div", props);
        this.table = props.table;
        this.populatedDOM = false;
        this._expanded = false;
        this._cells = [];
        this._element.classList.add(TableRowStyles.base);
        this.addClickListener((event: ClickEvent) => this.rowClicked(event.domEvent));
        this.addDblClickListener((event: ClickEvent) => this.rowClicked(event.domEvent));
        if (props?.virtualized !== false)
            this.addVirtualizedPlaceholder();
        (this._element as any).__row = this; // give a reference to the row in its element so we can easily access it from the observer
        if (this.table.selectionMode !== SelectionMode.NONE || this.table.addType === AddType.QUICK)
            this._element.classList.add(TableRowStyles.hoverShade);
        this._mode = TableRowMode.NONE;
        this.setProps(props);
    }

    public override addDblClickListener(value: any): Component {
        if (this.table?.isLookupTable) {
            return this;
        }
        return super.addDblClickListener(value);
    }

    public get table(): Table {
        return this._table;
    }

    private set table(value: Table) {
        this._table = value;
    }

    public get allowDelete(): boolean {
        if (this.isQuickAddRow())
            return false;
        return this._allowDelete !== false && this.table.allowDelete === true;
    }

    /**
     * Allows for control of if a single table row can be deleted.  In order for a row to be deletable, the enclosing
     * table must also be set to allow deletion (via the allowDelete property).
    */
    public set allowDelete(value: boolean) {
        if (value !== this._allowDelete) {
            this._allowDelete = value;
            this.syncToolsCell();
        }
    }

    public get allowEdit(): boolean {
        return this.rowBehavior.allowsEdit(this, this._allowEdit);
    }

    /**
     * Allows for control of if a single table row can be edited.  In order for a row to become editable, the enclosing
     * table must also be set to allow editing (via the allowEdit property).
    */
    public set allowEdit(value: boolean) {
        if (value !== this._allowEdit) {
            this._allowEdit = value;
            this.syncToolsCell();
        }
    }

    override setProps(props: Partial<TableRowProps>) {
        super.setProps(props);
    }

    public get data(): any {
        return this._data;
    }

    public set data(value: any) {
        if (this._data != null && this._data !== value && this.populatedDOM) {
            this.forEveryChildComponent((comp: Component) => {
                if (comp.boundRow === this._data) {
                    comp.boundRow = value;
                }
            });
        }
        this._data = value;
    }

    get placeholderHeightWidth(): HeightWidth {
        return this._placeholderHeightWidth;
    }

    set placeholderHeightWidth(value: HeightWidth) {
        this._placeholderHeightWidth = value;
        this.applyPlaceholderHeightWidth();
    }

    get expanded(): boolean {
        return this._expanded;
    }

    set expanded(value: boolean) {
        this.setExpandedAsync(value);
    }

    public async setExpandedAsync(value: boolean): Promise<Component | null> {
        if (value == null) {
            value = false;
        }

        if (this._expanded === value) {
            return this._expandPromise;
        }

        this._expanded = value;
        await this.syncExpanded();

        if (value) {
            await this.showExpansionElement();
            return this._expansionComponent;
        } else {
            await this.hideExpansionElement();
            return null;
        }
    }

    private showExpansionElement(): Promise<Component> {
        if (this._expanded && this._expansionComponent) {
            return Promise.resolve(this._expansionComponent);
        }

        this.buttonExpand?.setProps({ busy: true });
        this._expandPromise = this._expandPromise.then(async () => {
            const comp = await this.table._createExpansionComponent(this);
            this._setExpansionComponent(comp);
            return comp;
        }).finally(() => {
            this.buttonExpand?.setProps({ busy: false });
        });
        return this._expandPromise;
    }

    private async hideExpansionElement(): Promise<void> {
        this._expanded = false;
        this._expandPromise = this._expandPromise.then(async () => {
            this.table._removeExpansionElement(this);
            return null;
        });
    }

    /**
     * This method allows for Table to hide a row's expansion element when there is no user interation.  This is used at
     * times such as when all of the rows in the able are being cleared.  Row expand listeners will not be fired.
     *
     * This method should not be called from outside the library.
     */
    _hideExpansionElementSync() {
        this._expanded = false;
        this.table._removeExpansionElement(this, false);
        this.buttonExpand?.syncRotation();
    }

    private async syncExpanded(): Promise<void> {
        await this.populateDOMIfNeeded();
        this.buttonExpand?.syncRotation();
    }

    public getExpansionComponent(): Component {
        return this._expansionComponent;
    }

    _setExpansionComponent(value: Component) {
        if (value != null) {
            value.id += "-" + this.index;
        }
        this._expansionComponent = value;
        this.backgroundColors.providedExpandComponentColor = this._expansionComponent?.backgroundColor;
        this.syncExpandComponentBackgroundColor();
        this.addResizeListener(this.resizeExpansionComponent);
    }

    private resizeExpansionComponent = () => {
        if (this.expansionElement != null) {
            this.expansionElement.style.width = DOMUtil.getElementWidthString(this._element);
        }
    }

    public async getExpansionComponentLoaded(): Promise<Component | null> {
        if (this._expansionComponent) {
            return this._expansionComponent;
        }
        await this._expandPromise;
        return this._expansionComponent;
    }

    get expandable(): boolean {
        if (this._expandable === false) {
            return false;
        }
        return this.expandComponentDefined();
    }

    set expandable(value: boolean) {
        this._expandable = value;
        this.expandCell?.sync();
    }

    private expandComponentDefined(): boolean {
        return this.table.expandComponentDefined();
    }

    addVirtualizedPlaceholder() {
        this.placeholder = document.createElement("td");
        this.applyPlaceholderHeightWidth();
        this._element.appendChild(this.placeholder);
    }

    private applyPlaceholderHeightWidth() {
        if (this.placeholder != null) {
            if (this.placeholderHeightWidth == null) {
                this.placeholder.style.height = "";
                this.placeholder.style.width = "";
            }
            else {
                this.placeholder.style.height = `${this.placeholderHeightWidth.height}px`;
                this.placeholder.style.width = `${this.placeholderHeightWidth.width}px`;
            }
        }
    }

    get cells(): TableCell[] {
        return this._cells;
    }

    protected set cells(value: TableCell[]) {
        this._cells = value;
    }

    get additionalToolButtons(): Button[] {
        return ArrayUtil.getAsArray(this._additionalToolButtons, true);
    }

    private set additionalToolButtons(value: Button[]) {
        this._additionalToolButtons = value;
        this._additionalToolButtons?.forEach(button => this.applyDefaultToolProps(button));
    }

    setAdditionalToolProps(id: string, props: Partial<ButtonProps>) {
        this._additionalToolButtons?.find(button => button.id === id)?.setProps({ ...props });
    }

    async populateDOM(): Promise<void> {
        if (this._populatePromise == null) {
            this._populatePromise = this.internalPopulateDOM();
            AutomationUtil.addLoadPromise(this._populatePromise);
        }
        return this._populatePromise;
    }

    private async internalPopulateDOM() {
        delete this.placeholder;
        delete this.placeholderHeightWidth;
        this._element.innerHTML = "";
        const creationEvent = new TableRowCreationEvent(this, this.table);
        this.table.fireRowCreationListeners(creationEvent);
        this.additionalToolButtons = await this.table.deserializeAdditionalRowTools();
        for (const column of this.table.columns) {
            await this.populateDOMCell(column);
        }
        this.populatedDOM = true;
        this._updateCellsBasedOnSelection();
        const displayEvent = new TableRowDisplayEvent(this, this.table);
        this.table.fireRowDisplayListeners(displayEvent);
        this.displayInViewModeInternal();
        this.syncContextMenuListeners();
        // This seems redundant, but this set of the last scroll left is necessary to make the scroll set work when
        // displaying data.  I think it's because the table body doesn't have any rendered rows (that have populated
        // their DOM elements) when displayData() finishes.
        this.table.applyLastScrollLeft();
    }

    clear() {
        this._cells = [];
        this._element.innerHTML = "";
        this.populatedDOM = false;
    }

    columnsChanged() {
        if (this.populatedDOM === true) {
            this._cells = [];
            this._element.innerHTML = "";
            this.populateDOM();
        }
    }

    async populateDOMIfNeeded() {
        if (this.populatedDOM === true)
            return;
        await this.populateDOM();
    }

    async createCell(col: TableColumn): Promise<TableCell> {
        let cell: TableCell;
        if (col.cellDef != null) {
            const def = col.cellDef;
            const props = {
                ...def.cellProps,
                table: this.table,
                row: this,
                col: col,
                boundRow: this.data
            };
            const deserializeProps: DeserializeProps = {
                owner: def.owner,
                def: def.def,
                designer: this._designer,
                defaultPropValues: props
            };
            // Do not pass dataSources to deserialization; we don't want components in the row to be bound directly to
            // the DataSource
            cell = await new ComponentDeserializer(deserializeProps).deserializeSingleComponent() as TableCell;
        } else {
            cell = new TableCell({ col: col, boundRow: this.data });
            if (typeof col.cell === "function") {
                const props = col.cell(this, cell);
                if (Array.isArray(props)) { //props is an array of components
                    cell.add(...props);
                } else if (props._element != null) { //props is a single component
                    cell.add(props);
                } else {
                    cell.setProps(props);
                    if (props.id != null)
                        this[props.id] = cell;
                }
            } else {
                if (col.cell instanceof Component) {
                    CloneComponent.clone({
                        component: col.cell,
                        targetComponent: cell,
                        appendComponentId: this._designer == null
                    });
                    if (col.cell.id != null)
                        this[col.cell.id] = col.cell;
                } else {
                    cell.setProps(col.cell);
                    if (col.cell.id != null)
                        this[col.cell.id] = cell;
                }
            }

            await cell.ensureLoaded();
        }
        if (this.table._designer == null) {
            cell.displayData(this.data, this.table.data, this._index);
            if (this.table.printableToggleEnabled && cell.printableToggleEnabled)
                cell.printable = this.rowIsPrintable();
        }
        return cell;
    }

    private async populateDOMCell(col: TableColumn) {
        const cell = await this.createCell(col);
        this.addCell(cell);
    }

    addCell(cell: TableCell, index: number = -1) {
        cell.table = this.table;
        cell.row = this;
        cell._designer = this._designer;

        if (cell instanceof ExpandTableCell) {
            this.expandCell = cell;
            this.expandCell.sync();
            this._addCellToDOM(cell, index);
            // this.expandCell.row.setupStickyHover();
        } else if (cell instanceof ToolsTableCell) {
            this.toolsCell = cell;
            this.rowBehavior.addToolsCellToRow(this, this.toolsCell, index);
            this.syncToolsCell();
        } else {
            this._addCellToDOM(cell, index);
        }
    }

    /**
     * This method allows an existing table cell to be replaced with another cell.
     *
     * This method exists only for special cases and should not be used outside of the library.
     */
    _replaceCell(existingCell: TableCell, newCell: TableCell) {
        newCell.table = this.table;
        newCell.row = this;
        newCell._designer = this._designer;

        if (newCell instanceof ExpandTableCell) {
            this.expandCell = newCell;
            this.expandCell.sync();
            this.replaceCellInDOM(existingCell, newCell);
            // this.expandCell.row.setupStickyHover();
        } else if (newCell instanceof ToolsTableCell) {
            this.toolsCell = newCell;
            this.replaceCellInDOM(existingCell, newCell);
            this.syncToolsCell();
        } else {
            this.replaceCellInDOM(existingCell, newCell);
        }
    }

    _addCellToDOM(cell: TableCell, index: number = -1) {
        if (index < 0 || index >= this._cells.length) {
            this._cells.push(cell);
            this._element.appendChild(cell._element);
        } else {
            this._element.insertBefore(cell._element, this._cells[index]._element);
            this._cells.splice(index, 0, cell);
        }
    }

    private replaceCellInDOM(existingCell: TableCell, newCell: TableCell) {
        const index = this.cells.indexOf(existingCell);
        if (index >= 0) {
            this.cells[index] = newCell;
            this._element.replaceChild(newCell._element, existingCell._element);
        }
    }

    /**
     * Removes the column at the specified index.
     *
     * This should only be called via Table.ts, which will have validated that the removal of the column is allowed.
     */
    _removeColumn(index: number) {
        if (this.populatedDOM === true) {
            const cell = this.cells[index];
            this._element.removeChild(cell._element);
            this.cells.splice(index, 1);
        }
    }

    /**
     * Moves the column at the specified index.
     *
     * This should only be called via Table.ts, which will have validated that the move of the column is allowed.
     */
    _moveColumn(oldIndex: number, newIndex: number) {
        if (this.populatedDOM === true) {
            ArrayUtil.moveArrayElement(this.cells, oldIndex, newIndex);
            if (newIndex < oldIndex) {
                DOMUtil.moveDOMElementBefore(this._element.childNodes[oldIndex], this._element.childNodes[newIndex]);
            } else {
                DOMUtil.moveDOMElementAfter(this._element.childNodes[oldIndex], this._element.childNodes[newIndex]);
            }
            this.cells[newIndex]._updateAfterMove();
        }
    }

    /**
     * Switches the columns at the specified index, with each taking the place of the other.
     *
     * This should only be called via Table.ts, which will have validated that the switching of the columns is allowed.
     */
    _switchColumns(index1: number, index2: number) {
        if (this.populatedDOM === true) {
            ArrayUtil.switchArrayElements(this.cells, index1, index2);
            DOMUtil.switchDOMElements(this._element.childNodes[index1], this._element.childNodes[index2]);
        }
    }

    get mode(): TableRowMode {
        return this._mode;
    }

    set mode(value: TableRowMode) {
        if (value !== this._mode) {
            const oldMode = this._mode;
            this._mode = value;

            this.rowBehavior.manageEditStateOnModeChange(this, this._mode);
            this.syncToolsCell();
            const modeChangeEvent = new TableRowModeChangeEvent(this, this.table, oldMode, this._mode);
            this.table.fireRowModeChangeListeners(modeChangeEvent);
        }
    }

    rowBeingEdited(): boolean {
        return this._mode === TableRowMode.ADD || this._mode === TableRowMode.UPDATE;
    }

    rowIsPrintable(): boolean {
        return !this.table.printableToggleEnabled || this._mode === TableRowMode.NONE;
    }

    public isQuickAddRow(): boolean {
        return this.mode === TableRowMode.QUICK_ADD;
    }

    public isSearchRow(): boolean {
        return this.mode === TableRowMode.SEARCH;
    }

    public enableAllComponents(value: boolean) {
        this.cells.forEach(cell => cell.setPropOnChildren("enabled", value, false));
    }

    public get toolsCell(): ToolsTableCell {
        return this._toolsCell;
    }

    private set toolsCell(value: ToolsTableCell) {
        this._toolsCell = value;
    }

    public syncToolsCell() {
        this.toolsCell?.fill();
    }

    /**
     * Begins the drag-to-resequence action (when the drag handler image is clicked)
     *
     * @param event: the MouseEvent from the onmousedown action
     */
    _dragHandlerMouseDownListener(event: MouseEvent) {
        log.debug("Start row drag, moving row at index %o", this._index);
        event.preventDefault();
        this._createDragSession(event);
        // log.debug("Current row top: %o, next move up at %o, next move down at %o", this._element.getBoundingClientRect().top, this._dragSession.nextMoveUpAt, this._dragSession.nextMoveDownAt);
        this._element.style.zIndex = (Number.parseInt(this._dragSession.previousZIndex) + 1) + "";
        this._element.draggable = true;
        this.table.setTableBodyOnMouseMove((event: DomMouseEvent) => this._dragHandlerMouseMoveListener(event));
        document.onmouseup = (event: DomMouseEvent) => this._dragHandlerMouseUpListener(event);
    }

    /**
     * Creates a DragSession object to hold various values that are useful during the drag-to-resequence action
     *
     * @param event: the MouseEvent from the onmousedown action
     */
    private _createDragSession(event: MouseEvent) {
        this._dragSession = new DragSession();
        this._dragSession.oldRowIndex = this.index;
        this._dragSession.newRowIndex = this.index;
        this._dragSession.rowHeight = DOMUtil.getElementHeight(this._element);
        this._dragSession.initialRowY = this._element.getBoundingClientRect().top;
        this._dragSession.nextMoveUpAt = this._element.getBoundingClientRect().top - (this._dragSession.rowHeight / 2);
        this._dragSession.nextMoveDownAt = this._element.getBoundingClientRect().bottom + (this._dragSession.rowHeight / 2);
        this._dragSession.previousZIndex = this._element.style.zIndex;
        this._dragSession.dragStartY = event.domEvent.clientY;
    }

    /**
     * During the drag-to-resequence action:
     *    - move the row to its new position (by setting its top property relative to its starting position)
     *    - if the row being dragged moves high/low enough to displace another row, set the top property on that other row
     *    - keep track of:
     *         - how high/low the row must go before it displaces another row
     *         - the new index of the row being dragged
     *
     * @param event: the MouseEvent from the onmousemove action
     */
    private _dragHandlerMouseMoveListener(event: DomMouseEvent) {
        event.preventDefault();
        // log.debug(this, "Drag mouse move dragStartY: %o, event.clientY: %o", this._dragStartY, event.clientY);
        const topValue = event.clientY - this._dragSession.dragStartY;
        // log.debug(this, "New top value for row: %o, clientY: %o, dragStartY: %o", topValue, event.clientY, this._dragStartY);
        if ((topValue + this._dragSession.initialRowY) < this._dragSession.nextMoveUpAt) {
            this._dragSession.newRowIndex--;
            const otherRowToMove = this._getRowContainingY(this._dragSession.nextMoveUpAt);
            if (otherRowToMove != null) {
                const otherRowTop = DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("top", otherRowToMove));
                otherRowToMove.style.top = (otherRowTop + this._dragSession.rowHeight) + "px";
            }
            log.debug(this, "Row moved up, new row index: %o", this._dragSession.newRowIndex);
            this._dragSession.nextMoveUpAt -= this._dragSession.rowHeight;
            this._dragSession.nextMoveDownAt -= this._dragSession.rowHeight
            // log.debug("Current row top: %o, next move up at %o, next move down at %o", this._element.getBoundingClientRect().top, this._nextMoveUpAt, this._nextMoveDownAt);
        } else if ((topValue + this._dragSession.rowHeight + this._dragSession.initialRowY) > this._dragSession.nextMoveDownAt) {
            this._dragSession.newRowIndex++
            const otherRowToMove = this._getRowContainingY(this._dragSession.nextMoveDownAt);
            if (otherRowToMove != null) {
                const otherRowTop = DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("top", otherRowToMove));
                otherRowToMove.style.top = (otherRowTop - this._dragSession.rowHeight) + "px";
            }
            log.debug(this, "Row moved down, new row index: %o", this._dragSession.newRowIndex);
            this._dragSession.nextMoveUpAt += this._dragSession.rowHeight;
            this._dragSession.nextMoveDownAt += this._dragSession.rowHeight
            // log.debug(this, "Current row top: %o, next move up at %o, next move down at %o", this._element.getBoundingClientRect().top, this._nextMoveUpAt, this._nextMoveDownAt);
        }
        this._element.style.top = topValue + "px";
        // log.debug(this, "Drag mouse move new top value: %o", this._element.style.top);
    }

    /**
     * Find whatever row in the table contains the specified y position.  This helps to identify a row being displaced, so it can be moved.
     *
     * @param y: the y position value at which to look for a row
     */
    private _getRowContainingY(y: number): HTMLElement {
        for (const r of this.table.rows) {
            if (r._element === this._element)
                continue;
            const rect = r._element.getBoundingClientRect();
            if (rect.top < y && rect.bottom > y)
                return r._element;
        }
        return null;
    }

    /**
     * Ends the drag-to-resequence action (when the mouse button is released).  Removes temporary settings/values, including the DragSession object.
     *
     * @param _event: the MouseEvent from the onmouseup action
     */
    private _dragHandlerMouseUpListener(_event: DomMouseEvent) {
        log.debug(this, "End row drag")
        this._element.style.zIndex = this._dragSession.previousZIndex;
        this._element.draggable = false;
        this.table.moveRow(this, this._dragSession.newRowIndex, this._dragSession);
        this._dragSession = null;
        this.table.setTableBodyOnMouseMove(null);
        document.onmouseup = null;
    }

    editRow(event?: DomMouseEvent) {
        if (this.allowEdit === false) {
            return;
        }
        this.table.selectRow(this, SelectionType.SINGLE, event);
        if (this.isQuickAddRow() || this.mode === TableRowMode.SEARCH) {
            return;
        }
        this.mode = TableRowMode.UPDATE;
        if (this.data instanceof ModelRow) {
            this._originalData = this.data.createBasicCopy();
        } else {
            this._originalData = { ...this.data };
        }

        this.toolsCell?.hideOverlay();
    }

    cancelEdit() {
        if (this.mode === TableRowMode.UPDATE) {
            this.data = this._originalData.createBasicCopy();
            this._originalData = null;
            this.displayComponentValues();
            this.mode = TableRowMode.NONE;
        } else if (this.mode === TableRowMode.ADD) {
            if (this.table.data?.[this.index] === this.data)
                this.deleteRow();
            else
                this.table.removeRow(this.index);
        }
    }

    showEditLayout(addingRowInSlideout: boolean = false) {
        if (this.table.rowModeControlType === RowModeControlType.AUTO) {
            const editingRow = this.table.rows.find(row => row.rowBeingEdited());
            if (editingRow != null && editingRow !== this) {
                return;
            }
        }
        const erd = new EditRowDecorator({
            layout: Layout.getLayout(this.table.editLayout),
            layoutLoadListeners: this.table.onEditLayoutLoaded != null ? (event: Event) => this.table.onEditLayoutLoaded(erd, this) : null,
            width: this.table.editLayoutWidth,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            data: this._copyDataForSlideout(),
            dataSourceMode: this.mode === TableRowMode.SEARCH ? DataSourceMode.SEARCH : addingRowInSlideout ? DataSourceMode.ADD : DataSourceMode.UPDATE,
            onDelete: !this.isQuickAddRow() ? () => {
                this.deleteRow(erd);
            } : null,
            onSave: (updatedData: ModelRow | any) => this.saveChangesFromEdit(updatedData),
            onAddAnother: () => this.editClearNewRow(erd),
            onClose: (cancelled: boolean) => this.onSlideoutClose(erd, cancelled, addingRowInSlideout),
            doAfterSlideIn: (erd: EditRowDecorator) => {
                if (!addingRowInSlideout) {
                    erd.validateSimple();
                }
            }
        });
    }

    private _copyDataForSlideout(): ModelRow | any {
        if (this.data instanceof ModelRow)
            return this.data.createBasicCopy();
        return this.data;
    }

    public persistQuickAddRow() {
        if (!this.isQuickAddRow())
            return;
        this.saveChanges();
        this.updateDataFromComponents();
        this.table.saveQuickAddData();
    }

    public findFocusableComponent() {
        for (const cell of this.cells) {
            const comp = cell.findFocusableComponent();
            if (comp != null) {
                return comp;
            }
        }
        return undefined;
    }

    public findNonSelectCheckboxFocusableComponent() {
        for (const cell of this.cells) {
            if (!(cell instanceof SelectCheckboxTableCell)) {
                const comp = cell.findFocusableComponent();
                if (comp != null) {
                    return comp;
                }
            }
        }
        return undefined;
    }

    public displayComponentValues(): void {
        for (const cell of this.cells)
            if (cell.components != null)
                for (const comp of cell.components)
                    comp.displayData(this.data, this.table.data, this.index);
    }

    public hasChanges(): boolean {
        return this._originalData != null;
    }

    saveChanges(afterSave?: () => any): boolean {
        this.updateDataFromComponents();
        const event = new TableRowBeforeSaveEvent(this, this.table, this.table.getParentDataSource());
        this.table.fireRowBeforeSaveListeners(event);
        if (this.validateSimple(true) !== true) {
            return false;
        }
        if (this.data instanceof ModelRow && this.table.persistChangesImmediately) {
            const rowToSave = this.data as ModelRow;
            rowToSave.post().then(() => {
                if (afterSave) afterSave();
                if (!this.isQuickAddRow())
                    this.table.fireContentsChangedListener(new TableContentsChangedEvent(this.table, this, TableAction.UPDATE));
            });
        }
        else {
            if (!this.isQuickAddRow())
                this.table.fireContentsChangedListener(new TableContentsChangedEvent(this.table, this, TableAction.UPDATE));
        }
        this._originalData = null;

        if (this.table.rowModeControlType !== RowModeControlType.ALWAYS_EDIT)
            this.mode = TableRowMode.NONE;
        return true;
    }

    editClearNewRow(erd: EditRowDecorator) {
        this.table._createNewRowData().then(newRowData => {
            const addRowResult = this.table.addRow(newRowData, { mode: TableRowMode.ADD }, {
                display: false,
                addToData: true
            });
            addRowResult.row.showEditLayout(true);
            this.onSlideoutClose(erd, false, true);
        });
    }

    saveChangesFromEdit(updatedData: ModelRow | any) {
        if (this.mode === TableRowMode.SEARCH) {
            this.data.setValuesAndLookupModelData(updatedData);
            this.displayComponentValues();
        } else if (this.isQuickAddRow()) {
            this.data.setValuesAndLookupModelData(updatedData);
            const addRowResult = this.table.addRow(this.data, { mode: TableRowMode.ADD }, {
                display: true,
                addToData: true,
                save: false
            }); //save will happen afterward
            if (addRowResult.row.saveChanges()) {
                this.table.resetQuickAddRow();
            }
        } else {
            this.data.setValuesAndLookupModelData(updatedData);
            this.saveChanges();
            if (this.mode === TableRowMode.ADD)
                this.table.displayRow(this);
        }
        this.table.resetFilter();
    }

    onSlideoutClose(erd: EditRowDecorator, cancelled: boolean, addingRowInSlideout: boolean) {
        if (cancelled === true && addingRowInSlideout === true)
            this.deleteRow(erd);
        else
            erd.slideOut();
    }

    public updateDataFromComponents(): void {
        for (const cell of this.cells)
            if (cell.components != null)
                for (const comp of cell.components)
                    comp.updateBoundData(this.data, DataSourceMode.UPDATE);
    }

    private updateDataToComponents(addedRow: TableRow): void {
        let cells: TableCell[];
        let data: any;
        if (addedRow != null) {
            cells = addedRow.cells;
            data = addedRow.data;
        } else {
            cells = this.cells;
            data = this.data;
        }

        for (const cell of cells)
            if (cell.components != null)
                for (const comp of cell.components)
                    comp.displayData(data, data, 0);
    }

    deleteRow(erd?: EditRowDecorator) {
        if (this.table.dataSource?.parentDataSource != null && !this.table.persistChangesImmediately) {
            this.internalDeleteRow(erd);
        } else {
            this.table.confirmRowDelete(this).then(async clickedYes => {
                if (clickedYes) {
                    if (this.table.persistChangesImmediately) {
                        await this.data.delete();
                    }
                    this.internalDeleteRow(erd);
                }
            });
        }
    }

    private internalDeleteRow(erd?: EditRowDecorator) {
        this.toolsCell?.hideOverlay();
        this.table.deleteRow(this);
        erd?.slideOut();
    }

    private rowClicked(event: DomMouseEvent) {
        if (this.table != null) {
            if (this.table.allowsInlineEditing) {
                this.editRowOnClick(event);
                this.table.activateValidateOnOutsideClick(event as PointerEvent);
            } else if (this.table.rowModeControlType !== RowModeControlType.ALWAYS_EDIT) {
                // Do not unselect the row on double click
                if (event.detail === 2 && this.selected) {
                    return;
                }
                let selType = SelectionType.SINGLE;
                if (event.shiftKey) {
                    selType = SelectionType.RANGE;
                    event.preventDefault();
                } else if (event.ctrlKey || event.metaKey) {
                    selType = SelectionType.DISTINCT;
                }
                this.table.selectRow(this, selType, event);
            }
        }
    }

    private editRowOnClick(event: DomMouseEvent) {
        const isSelectedQuickAddOrSearchRow = (this.isQuickAddRow() || this.isSearchRow()) && this.selected;

        if (!this.rowBeingEdited() && !isSelectedQuickAddOrSearchRow && this.table.completeEditedRows()) {
            this.editRow(event);
        }
    }

    validateSimple(checkRequired: boolean = true, showErrors: boolean = true): boolean {
        const validationResults: ValidationResult[] = this.validate(checkRequired, showErrors);
        if (validationResults == null) {
            return true;
        }
        for (const validationResult of validationResults) {
            if (validationResult.isValid !== true) {
                return false;
            }
        }
        return true;
    }

    validate(checkRequired: boolean, showErrors: boolean = true): ValidationResult[] {
        let result: ValidationResult[] = null;
        for (const cell of this.cells) {
            if (cell === this.toolsCell || cell === this.expandCell) {
                continue;
            }
            const thisResult = cell.validate(checkRequired, showErrors);
            if (thisResult !== null) {
                if (result == null)
                    result = thisResult;
                else
                    result = result.concat(thisResult);
            }
        }
        return result;
    }

    get index(): number {
        return this._index;
    }

    set index(value: number) {
        this._index = value;
    }

    public override get backgroundColor(): Color {
        return super.backgroundColor;
    }

    public override set backgroundColor(value: Color) {
        this.backgroundColors.providedRowColor = value;
        this.syncRowBackgroundColor();
        this.syncExpandComponentBackgroundColor();
    }

    set evenOddColor(value: Color) {
        this.backgroundColors.evenOddColor = value;
        this.syncRowBackgroundColor();
        this.syncExpandComponentBackgroundColor();
    }

    private get backgroundColors(): TableRowBackgroundColors {
        this.initBackgroundColors();
        return this._backgroundColors;
    }

    private initBackgroundColors() {
        if (this._backgroundColors == null) {
            this._backgroundColors = new TableRowBackgroundColors(this);
        }
    }

    private syncRowBackgroundColor() {
        const effectiveColor = this.backgroundColors.effectiveRowColor;
        if (effectiveColor != super.backgroundColor) {
            super.backgroundColor = effectiveColor;
            // if (this.expandCell != null) {
            //     this.expandCell.backgroundColor = effectiveColor;
            // }
        }
    }

    private syncExpandComponentBackgroundColor() {
        this.getExpansionComponentLoaded().then(expandComponent => {
            if (expandComponent != null) {
                const newEffectiveColor = this.backgroundColors.effectiveExpandComponentColor;
                if (newEffectiveColor != expandComponent.backgroundColor) {
                    expandComponent.backgroundColor = newEffectiveColor;
                }
            }
        });
    }

    // private setupStickyHover() {
    //     this.addMouseEnterListener(this.applyStickyHover);
    //     this.addMouseLeaveListener(this.removeStickyHover);
    // }

    // private applyStickyHover = () => {
    //     this.cells.forEach(cell => {
    //         if (cell._element.classList.contains('sticky-cell') && !this.selected) {
    //             cell._element.style.background = TableRowStylesUtil.HOVER_BACKGROUND_CSS;
    //         }
    //     });
    // }

    // private removeStickyHover = () => {
    //     this.cells.forEach(cell => {
    //         if (cell._element.classList.contains('sticky-cell')) {
    //             // Restore the background to match the row's current background
    //             cell.backgroundColor = cell.backgroundColor;
    //         }
    //     });
    // }

    get selected(): boolean {
        return this._selected;
    }

    set selected(value: boolean) {
        if (value === this._selected) {
            return;
        }
        this._selected = value;
        this.syncRowBackgroundColor();
        this.syncExpandComponentBackgroundColor();
        if (this.populatedDOM === true) {
            this._updateCellsBasedOnSelection();
        }
    }

    private _updateCellsBasedOnSelection() {
        if (this.selected == null)
            return;
        for (const cell of this.cells)
            this._updateCellSelection(cell);
    }

    private _updateCellSelection(cell: TableCell) { // Move along.  Nothing to see here.
        if (!(cell instanceof Container))
            return;
        const comps: Component[] = cell.getRecursiveChildren();
        for (const comp of comps) {
            if (!(comp instanceof Button)) {
                if (this.selected) {
                    if (this.table.addType === AddType.QUICK && comp instanceof Textbox && comp._inputDiv != null && comp.enabled) {
                        let color = getThemeForKey("component.palette.table.editRow.textbox.backgroundColor");
                        color = getThemeColor(color) || color;
                        comp._inputDiv.style.backgroundColor = color;
                    }
                }
            }
        }
    }

    override getPropertyDefinitions() {
        return TableRowPropDefinitions.getDefinitions();
    }

    public get populatedDOM() {
        return this._populatedDOM;
    }

    protected set populatedDOM(value: boolean) {
        this._populatedDOM = value;
        if (this._populatedDOM === false) {
            this._populatePromise = null;
        }
    }

    override getSearchValues(): string[] {
        const result = [];
        for (const cell of this.cells) {
            result.push(...cell.getSearchValues());
        }
        return result;
    }

    setPropOnChildren(propName: string, value: any) {
        for (const cell of this.cells) {
            if (cell instanceof TableCell) {
                cell.setPropOnChildren(propName, value);
            }
        }
    }

    setBorderBottom(isExpanding: boolean) {
        if (isExpanding === true) {
            this._unexpandedBorderBottomWidth = DOMUtil.getComputedStyle("border-bottom-width", this._element);
            this._element.style.borderBottomWidth = "0px";
        } else
            this._element.style.borderBottomWidth = this._unexpandedBorderBottomWidth;
    }

    override get serializationName() {
        return "tablerow";
    }

    override get properName(): string {
        return "Table Row";
    }

    override get draggable(): boolean {
        return this.table.rowsAreDraggable();
    }

    override set draggable(value: boolean) {
        this.syncToolsCell();
    }

    public static getContainingTableRow(component: Component): TableRow {
        if (component == null)
            return null;
        if (component instanceof TableCell)
            return component.row;
        return this.getContainingTableRow(component.parent);
    }

    public findComponentById(id: String | Function): Component {
        let result: Component;
        for (const cell of this.cells) {
            result = cell.findComponentById(id);
            if (result != null)
                break;
        }
        return result;
    }

    public findComponentsByField(field: string): Component[] {
        const components: Component[] = [];
        for (const cell of this.cells) {
            const cellComponents = cell.findComponentByField(field);
            for (const cellComponent of cellComponents) {
                components.push(cellComponent);
            }
        }
        return components;
    }

    public findComponentsByType<T extends Component>(type: new (...args: any[]) => T): T[] {
        const components: T[] = [];
        for (const cell of this.cells) {
            cell.findComponentsByType(type).forEach(comp => components.push(comp));
        }
        return components;
    }

    public forEveryChildComponent(callback: (component: Component) => void) {
        for (const cell of this.cells)
            cell.forEveryChildComponent(callback);
    }

    displayDataForField(event: FieldUpdateEvent, data: ModelRow, allData: ModelRow[], rowIndex: number) {
        const components = this.findComponentsByField(event.fieldName);
        for (const component of components) {
            if (component !== event.originator)
                component.displayData(data, allData, rowIndex);
        }
    }

    getEffectiveDataSourceMode(): DataSourceMode {
        if (this.mode === TableRowMode.ADD || this.isQuickAddRow())
            return DataSourceMode.ADD;
        if (this.mode === TableRowMode.UPDATE)
            return DataSourceMode.UPDATE;
        if (this.mode === TableRowMode.SEARCH)
            return DataSourceMode.SEARCH;
        return DataSourceMode.NONE;
    }

    isFirstCellInRow(cell: TableCell): boolean {
        if (cell == null || this.cells == null)
            return false;
        const cellIndex = this.getIndexOfCell(cell);
        return cellIndex === 0;
    }

    isLastCellInRow(cell: TableCell): boolean {
        if (cell == null || this.cells == null)
            return false;
        const cellIndex = this.getIndexOfCell(cell);
        return cellIndex === this.cells.length - 1;
    }

    private getIndexOfCell(cell: TableCell): number {
        if (cell == null || this.cells == null)
            return -1;
        for (let index = 0; index < this.cells.length; index++) {
            if (cell === this.cells[index])
                return index;
        }
        return -1;
    }

    public override scrollIntoView(options?: Partial<ScrollOptions>, startingElement?: HTMLElement) {
        if (this.expanded !== true)
            DOMUtil.scrollElementIntoView(this._element, options);
        else {
            if (options == null)
                options = {};
            if (options.block == null) {
                if (this.table.getBodyHeight() < this.expansionElementHeight)
                    options.block = Block.START;
                else
                    options.block = Block.END;
            }
            DOMUtil.scrollElementIntoView(this.expansionElement, options);
        }
    }

    public get buttonExpand(): ExpandButton {
        return this.expandCell?.buttonExpand;
    }

    displayingExpandCellRight(): boolean {
        return this.table.displayingExpandCellRight();
    }

    public getRowElementHeight(): number {
        return DOMUtil.getElementHeight(this._element);
    }

    public get expansionElement(): HTMLTableRowElement {
        return this._expansionElement;
    }

    _setExpansionElement(value: HTMLTableRowElement) {
        this._expansionElement = value;
        if (value != null) {
            value.style.width = DOMUtil.getElementWidthString(this._element);
        }
    }

    private get expansionElementHeight(): number {
        if (this.expansionElement == null) {
            return 0;
        }
        return DOMUtil.getElementHeight(this.expansionElement);
    }

    public hideDragHandle() {
        this.toolsCell?.hideDragHandle();
    }

    public showDragHandle() {
        this.toolsCell?.showDragHandle();
    }

    /**
     * This method will collect key handlers from this row and its child components.
     *
     * @returns void
     */
    fillKeyHandlerGroup(recreate: boolean = false) {
        if (this._keyHandlerGroup != null && recreate !== true)
            return;
        this._initKeyHandlerGroup(recreate);
        this._addKeyHandlersToGroup(this.getKeyHandlers());
        for (const cell of this.cells) {
            this._addKeyHandlersToGroup(cell.getKeyHandlers());
            this._addKeyHandlersToGroup(cell.getChildrenKeyHandlersRecursive());
        }
        // When first rendered, we fill keyHandlerGroup after the toolsCell is created, but before the tools cell is
        // added to the cells array.  In that case gather its keys directly.
        if (this.toolsCell != null && this.cells.includes(this.toolsCell) === false)
            this._addKeyHandlersToGroup(this.toolsCell.getKeyHandlers());
        this._keyHandlerGroup.sort();
    }

    override getKeyHandlers(): KeyHandler[] {
        if (this.isQuickAddRow()) {
            const listener = () => this.persistQuickAddRow();
            const enterKeyHandler = this.createKeyHandler(Keys.ENTER, listener, null, this._element);
            const ctrlEnterKeyHandler = this.createKeyHandler(Keys.ENTER, listener, { ctrlKey: true }, this._element);
            this.addKeyHandler(enterKeyHandler);
            this.addKeyHandler(ctrlEnterKeyHandler);
        }
        return super.getKeyHandlers();
    }

    private _addKeyHandlersToGroup(compKeyHandlers: KeyHandler[]) {
        if (ArrayUtil.isEmptyArray(compKeyHandlers) === true)
            return;
        for (const compKeyHandler of compKeyHandlers) {
            this._keyHandlerGroup.addKeyHandler(compKeyHandler);
        }
    }

    private _initKeyHandlerGroup(recreate: boolean = false) {
        if (this._keyHandlerGroup == null || recreate === true)
            this._keyHandlerGroup = new KeyHandlerGroup();
    }

    handleKey(event: KeyEvent): boolean {
        this.fillKeyHandlerGroup();
        return this._keyHandlerGroup.handleKey(event);
    }

    public forEveryTableCell(callback: (cell: TableCell) => void) {
        for (const cell of this.cells) {
            callback(cell);
        }
    }

    public override doBeforeComponentEnlarge(cellsToEnlarge: TableCell[]) {
        this.hiddenCells = [];
        this.forEveryTableCell((cell: TableCell) => {
            if (cell.visible === true && cellsToEnlarge.includes(cell) !== true) {
                this.hiddenCells.push(cell);
                cell.visible = false;
            }
        });
        this.enlarged = true;
    }

    public override doAfterComponentsShrink() {
        this.enlarged = false;
        for (const hiddenCell of this.hiddenCells) {
            hiddenCell.visible = true;
        }
        this.hiddenCells = undefined;
    }

    public shrinkAllComponents() {
        for (const cell of this.cells) {
            cell.enlarged = false;
        }
    }

    override handleEnlargeOrShrink() {
        if (this.enlarged === true) {
            //allow the one row to fill the table body
            this.applyTempState({ minHeight: this.table.getBodyHeight() });
        } else {
            //shrink the row
            this.revertTempState();
        }
    }

    override get enlargeScope(): Component {
        if (super.enlargeScope != null)
            return super.enlargeScope;
        super.enlargeScope = this.table; //this.table.getEnlargeScope();
        return super.enlargeScope;
    }

    public set enlargeScope(value: Component) {
        super.enlargeScope = value;
    }

    applyDefaultToolProps(button: Button) {
        const defaultToolButtonProps: Partial<ButtonProps> = {
            imageName: ImageName.formEdit,
            variant: ButtonVariant.round,
            imageHeight: 18,
            imageWidth: 18,
            focusable: false,
            color: "primary",
            rowBreak: false,
            caption: ""
        };

        Object.entries(defaultToolButtonProps).forEach(([key, value]) => {
            if ((key === "imageName" || key === "color") && button[key] != null) {
                return;
            }
            button[key] = value;
        });

        button.addPropertyDefaultValueOverrides({ ...defaultToolButtonProps, margin: 4, padding: 0 });

        if (this._designer == null) {
            button.addClickListener(() => this.toolsCell?.hideToolsViewer(), false, true)
        }
    }

    override get _designer(): DesignerInterface {
        return super._designer;
    }

    override set _designer(value: DesignerInterface) {
        super._designer = value;
        this.cells.forEach(cell => cell._designer = value);
    }

    private get rowBehavior(): TableRowBehavior {
        TableRow._rowBehavior ??= BehaviorTypes.get("TableRow");
        return TableRow._rowBehavior;
    }

    override displayInViewMode() {
        this.displayInViewModeInternal();
    }

    displayInViewModeInternal() {
        if (this.table.displayingInViewMode === true && this.populatedDOM) {
            this.cells.forEach(cell => cell.displayInViewMode());
            this.getExpansionComponent()?.displayInViewMode();
            this.additionalToolButtons = [];
            this.syncToolsCell();
        }
    }

    public get contextMenuItems(): ContextMenuItemsProvider {
        // Only allow provided context menu items for a table row when that row is selected
        return this.table.selectedRows?.includes(this) === true ? super.contextMenuItems : null;
    }

    public set contextMenuItems(value: ContextMenuItemsProvider) {
        super.contextMenuItems = value;
    }

    override showContextMenu(event: DomEvent | Event) {
        if (this.table.selectionMode !== SelectionMode.NONE && !this.table.selectedRows?.includes(this)) {
            this.table.selectRow(this, SelectionType.SINGLE, Event.resolveDomEvent(event));
        }
        super.showContextMenu(event);
    }

    override finalizeContextMenuLabelProps(labelProps: Partial<ContextMenuLabelProps>[]) {
        const openContextMenuItems = this.table.crudManager.getOpenContextMenuItems(this)
        if (!ArrayUtil.isEmptyArray(openContextMenuItems)) {
            // Make sure the last item to display before the 'open' items displays the separator
            const visibleLabelProps = (labelProps ?? []).filter(labelProp => labelProp.visible !== false)
            const lastVisible = ArrayUtil.getLastElement(visibleLabelProps) as Partial<ContextMenuLabelProps>;
            if (lastVisible != null) {
                lastVisible.separatorAfter = true;
            }
            labelProps.push(...openContextMenuItems);
        }
    }

    public selectAndScrollIntoView() {
        this.populateDOMIfNeeded().then(() => {
            this.table.selectRow(this, SelectionType.SINGLE, null);
            this.scrollIntoView();
        });
    }
}

export class DragSession {
    private _dragStartY: number;
    private _rowHeight: number;
    private _nextMoveUpAt: number;
    private _nextMoveDownAt: number;
    private _initialRowY: number;
    private _newRowIndex: number;
    private _oldRowIndex: number;
    private _previousZIndex: string;

    get dragStartY(): number {
        return this._dragStartY;
    }

    set dragStartY(value: number) {
        this._dragStartY = value;
    }

    get rowHeight(): number {
        return this._rowHeight;
    }

    set rowHeight(value: number) {
        this._rowHeight = value;
    }

    get nextMoveUpAt(): number {
        return this._nextMoveUpAt;
    }

    set nextMoveUpAt(value: number) {
        this._nextMoveUpAt = value;
    }

    get nextMoveDownAt(): number {
        return this._nextMoveDownAt;
    }

    set nextMoveDownAt(value: number) {
        this._nextMoveDownAt = value;
    }

    get initialRowY(): number {
        return this._initialRowY;
    }

    set initialRowY(value: number) {
        this._initialRowY = value;
    }

    get newRowIndex(): number {
        return this._newRowIndex;
    }

    set newRowIndex(value: number) {
        this._newRowIndex = value;
    }

    get oldRowIndex(): number {
        return this._oldRowIndex;
    }

    set oldRowIndex(value: number) {
        this._oldRowIndex = value;
    }

    get previousZIndex(): string {
        return this._previousZIndex;
    }

    set previousZIndex(value: string) {
        this._previousZIndex = value;
    }
}

ComponentTypes.registerComponentType("tablerow", {
    constructor: TableRow.prototype.constructor
});
